import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const COOKIE_NAME = 'rejumist_ref';
const SESSION_KEY = 'rejumist_ref_session';
const COOKIE_MAX_AGE_DAYS = 30;

const AffiliateContext = createContext(null);

export const useAffiliate = () => {
  const ctx = useContext(AffiliateContext);
  if (!ctx) throw new Error('useAffiliate must be used within AffiliateProvider');
  return ctx;
};

// Cookie helpers (lightweight)
const setCookie = (name, value, days) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
};

const getCookie = (name) => {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : '';
};

// Parse cookie payload: "CODE|ISO_TIMESTAMP"
const parseCookie = () => {
  const raw = getCookie(COOKIE_NAME);
  if (!raw) return { code: '', firstClickAt: '' };
  const [code, firstClickAt] = raw.split('|');
  return { code: (code || '').toUpperCase(), firstClickAt: firstClickAt || '' };
};

export const AffiliateProvider = ({ children }) => {
  const trackedRef = useRef(false);

  // Read the current session's ref (Rule 2) — set for this browser session only
  const getSessionRef = useCallback(() => (sessionStorage.getItem(SESSION_KEY) || '').toUpperCase(), []);

  // Read the first-click cookie (Rule 1)
  const getCookieRef = useCallback(() => parseCookie(), []);

  // Snapshot to send with checkout — includes both session_ref & cookie_ref
  const getAttributionSnapshot = useCallback(() => {
    const { code, firstClickAt } = parseCookie();
    return {
      affiliate_session_ref: (sessionStorage.getItem(SESSION_KEY) || '').toUpperCase(),
      affiliate_cookie_code: code,
      affiliate_cookie_first_click_at: firstClickAt,
    };
  }, []);

  // Capture ?ref= from the URL on mount and on any route change (we hook via useEffect once — SPA rendering re-runs effects)
  useEffect(() => {
    if (trackedRef.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim().toUpperCase();
      if (ref) {
        // Always update session (Rule 2: current session overrides at checkout)
        sessionStorage.setItem(SESSION_KEY, ref);
        // Set first-click cookie ONLY if none exists yet (first-click wins)
        const existing = parseCookie();
        if (!existing.code) {
          setCookie(COOKIE_NAME, `${ref}|${new Date().toISOString()}`, COOKIE_MAX_AGE_DAYS);
        }
        // Fire-and-forget server-side click log (records email→code binding once user is known)
        axios.post(`${API}/api/affiliate/track`, { code: ref }, { withCredentials: true }).catch(() => {});
        trackedRef.current = true;
      }
    } catch (e) { /* no-op */ }
  }, []);

  // Called by AuthContext / checkout to associate an email with the affiliate click server-side
  const bindEmail = useCallback(async (email) => {
    const { code } = parseCookie();
    const session = (sessionStorage.getItem(SESSION_KEY) || '').toUpperCase();
    const effectiveCode = session || code;
    if (!effectiveCode || !email) return;
    try {
      await axios.post(`${API}/api/affiliate/track`, { code: effectiveCode, email }, { withCredentials: true });
    } catch (e) { /* no-op */ }
  }, []);

  return (
    <AffiliateContext.Provider value={{ getSessionRef, getCookieRef, getAttributionSnapshot, bindEmail }}>
      {children}
    </AffiliateContext.Provider>
  );
};
